import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { authApi, isAuthenticationError, setApiAccessToken } from '../services/api';

const AuthContext = createContext(null);
let refreshBootstrapPromise = null;

const getRefreshBootstrapPromise = () => {
  if (refreshBootstrapPromise) {
    return refreshBootstrapPromise;
  }

  refreshBootstrapPromise = authApi.refresh().finally(() => {
    queueMicrotask(() => {
      refreshBootstrapPromise = null;
    });
  });

  return refreshBootstrapPromise;
};

const getLocationState = (location) => ({
  pathname: location.pathname,
  search: location.search,
  hash: location.hash,
});

const mapAuthResponseToUser = (response) => ({
  userId: response.userId,
  email: response.email,
  username: response.username,
  role: response.role,
});

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [authState, setAuthState] = useState({
    status: 'bootstrapping',
    user: null,
    accessToken: null,
  });

  const clearSession = useCallback((status = 'anonymous') => {
    setApiAccessToken(null);
    setAuthState({
      status,
      user: null,
      accessToken: null,
    });
  }, []);

  const applyAuthResponse = useCallback((response) => {
    const nextUser = mapAuthResponseToUser(response);
    const nextAccessToken = response.accessToken;

    setApiAccessToken(nextAccessToken);
    setAuthState({
      status: 'authenticated',
      user: nextUser,
      accessToken: nextAccessToken,
    });

    return response;
  }, []);

  useEffect(() => {
    let isActive = true;

    const bootstrapSession = async () => {
      try {
        const response = await getRefreshBootstrapPromise();

        if (!isActive) {
          return;
        }

        applyAuthResponse(response);
      } catch (error) {
        if (!isActive) {
          return;
        }

        clearSession('anonymous');
      }
    };

    bootstrapSession();

    return () => {
      isActive = false;
    };
  }, [applyAuthResponse, clearSession]);

  const login = useCallback(async ({ email, password }) => {
    const response = await authApi.login({ email, password });
    return applyAuthResponse(response);
  }, [applyAuthResponse]);

  const register = useCallback(async ({ token, username, password }) => {
    const response = await authApi.register({ token, username, password });
    return applyAuthResponse(response);
  }, [applyAuthResponse]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearSession('anonymous');
      navigate('/', { replace: true });
    }
  }, [clearSession, navigate]);

  const handleProtectedRequest = useCallback(async (requestFn) => {
    try {
      return await requestFn();
    } catch (error) {
      if (isAuthenticationError(error)) {
        clearSession('anonymous');
        navigate('/login', {
          replace: true,
          state: {
            from: getLocationState(location),
            authMessage: 'Your session has ended. Please log in again.',
          },
        });

        throw new Error('Your session has ended. Please log in again.');
      }

      throw error;
    }
  }, [clearSession, location, navigate]);

  const value = useMemo(() => ({
    status: authState.status,
    user: authState.user,
    accessToken: authState.accessToken,
    isAuthenticated: authState.status === 'authenticated',
    isAdmin: authState.user?.role === 'ADMIN',
    login,
    register,
    logout,
    handleProtectedRequest,
  }), [authState, handleProtectedRequest, login, logout, register]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
